import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { CheckCircle2, Copy } from 'lucide-react';
import { crmFetch } from './crmApi';

const ROLES = [
  { value: 'client_recruiter', label: 'Recruiter' },
  { value: 'client_admin', label: 'Admin Recruiter' },
];

export const InvitePlatformModal = ({ open, onOpenChange, defaultOrgId = '', onInvited }) => {
  const [orgs, setOrgs] = useState([]);
  const [form, setForm] = useState({ email: '', role: 'client_recruiter', organizationId: defaultOrgId });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null); // { registrationUrl, emailSent }

  useEffect(() => {
    if (!open) return;
    setSent(null);
    setForm({ email: '', role: 'client_recruiter', organizationId: defaultOrgId });
    (async () => {
      const res = await crmFetch('/organizations');
      if (res.success) setOrgs(res.data || []);
    })();
  }, [open, defaultOrgId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.email.trim()) { toast.error('Email is required'); return; }
    if (!form.organizationId) { toast.error('Please select a company'); return; }
    setSending(true);
    try {
      const res = await crmFetch('/platform-invite', { method: 'POST', body: JSON.stringify(form) });
      if (!res.success) { toast.error(res.message || 'Failed to send invite'); return; }
      toast.success(res.message || 'Invitation sent');
      setSent({ registrationUrl: res.data?.registrationUrl, emailSent: res.emailSent });
      onInvited && onInvited();
    } finally { setSending(false); }
  };

  const copyLink = () => {
    if (sent?.registrationUrl) { navigator.clipboard?.writeText(sent.registrationUrl); toast.success('Onboarding link copied'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="invite-platform-modal">
        <DialogHeader>
          <DialogTitle>Invite to Platform</DialogTitle>
          <DialogDescription>Invite a recruiter or admin recruiter to a client company. They'll receive an email to onboard.</DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="space-y-4" data-testid="invite-success">
            <div className="flex items-start gap-3 rounded-lg bg-emerald-50 border border-emerald-200 p-4">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <div className="text-sm text-emerald-800">
                {sent.emailSent ? 'Invitation email sent successfully.' : 'Invitation created. Email could not be sent — share the link below.'}
              </div>
            </div>
            {sent.registrationUrl && (
              <div>
                <Label className="mb-1.5 block text-xs">Onboarding link</Label>
                <div className="flex gap-2">
                  <Input readOnly value={sent.registrationUrl} className="text-xs" data-testid="invite-link-input" />
                  <Button variant="outline" size="icon" onClick={copyLink} data-testid="invite-copy-btn"><Copy className="w-4 h-4" /></Button>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSent(null)} data-testid="invite-another-btn">Invite Another</Button>
              <Button onClick={() => onOpenChange(false)} className="bg-blue-600 hover:bg-blue-700" data-testid="invite-done-btn">Done</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block">Company *</Label>
              <Select value={form.organizationId} onValueChange={(v) => set('organizationId', v)}>
                <SelectTrigger data-testid="invite-org-select">
                  <SelectValue placeholder="Select a company">
                    {orgs.find((o) => o.id === form.organizationId)?.name || 'Select a company'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {orgs.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">No companies yet — add one first.</div>}
                  {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">Role *</Label>
              <Select value={form.role} onValueChange={(v) => set('role', v)}>
                <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="person@company.com" data-testid="invite-email-input" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="invite-cancel-btn">Cancel</Button>
              <Button onClick={submit} disabled={sending} className="bg-blue-600 hover:bg-blue-700" data-testid="invite-send-btn">
                {sending ? 'Sending…' : 'Send Invite'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
